import {type RunsDataQueryConfig} from '../../../../containers/RunsDataLoader';
import * as Filter from '../../../../util/filters';
import {
  excludedRunIdsFilter,
  pinnedRunIdsFilter,
} from '../../../../util/hooks/usePinnedRunsFilters';
import {isSystemMetric} from '../../../../util/metrics';
import {type DataDogPanelType} from '../../../../util/panelTypes';
import {sortToOrderString} from '../../../../util/queryts';
import {type SingleQuery} from '../../../../util/queryTypes';
import {type PrimaryLineType} from '../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import type * as Run from '../../../../util/runs';
import {GROUP_BY_ALL_KEY} from '../../../../util/runTypes';
import {runIdsToNames} from '../../../../util/selectionmanager';

export type BucketedQueryType =
  | 'BucketedQuery'
  | 'BucketedQueryId'
  | 'BucketedBackendLinesQuery'
  | 'BucketedBackendLinesQueryId'
  | 'GroupedBucketedQuery'
  | 'GroupedBucketedQueryId'
  | 'GroupedBucketedBackendLinesQuery';

export type QueryVars = {
  bins?: number; // not used by the backend, included for datadog segmentation
  bucketedHistorySpecs: string[];
  configKeys: string[] | undefined;
  enableConfig: boolean;
  enableSummary: boolean;
  entityName: string;
  excludeOutliers?: boolean;
  expressions: string[] | undefined;
  filters: string;
  groupKeys: string[];
  groupLevel: number;
  groupingDepth?: number;
  includePinnedRuns: boolean;
  internalId: string;
  isSystemMetrics: boolean | null; // not used, included for datadog segmentation
  limit: number;
  maxGroups: number | undefined;
  order: string;
  panelType: DataDogPanelType; // client-only, like bins
  pinnedRunFilters: string | undefined;
  pinnedRunsLimit: number;
  projectName: string;
  summaryKeys: string[] | undefined;
  timeout?: number; // Frontend-specified timeout in seconds for progressive loading
  primaryLineType?: PrimaryLineType;
  xAxisScale?: 'log';
};

export function queryVars(
  runsDataQuery: Exclude<RunsDataQueryConfig, 'runsetQueries'>,
  query: SingleQuery,
  nBuckets: number,
  queryType: BucketedQueryType,
  expressions: string[] | undefined,
  panelType: DataDogPanelType,
  timeout?: number
): QueryVars {
  const order = sortToOrderString(query.sort);

  let limit = runsDataQuery.page?.size || 10;
  if (Array.isArray(limit)) {
    console.error('Unexpected query limit of type array', {
      entityName: runsDataQuery.entityName,
      projectName: runsDataQuery.projectName,
    });
    limit = limit.flat(Infinity)[0] ?? 10;
  }

  // Replace config.None with GROUP_BY_ALL_KEY because config.None is not a valid group key when grouping in the backend
  // This happens when selecting "None" in the group by dropdown
  // TODO: Move this replacement upstream to where groupKeys are initially populated, once we have sufficient test
  // coverage to safely make that change.
  const groupKeys = [
    'GroupedBucketedQuery',
    'GroupedBucketedQueryWithInternalId',
    'GroupedBucketedBackendLinesQuery',
  ].includes(queryType)
    ? (runsDataQuery.groupKeys?.map(k =>
        k === 'config.None' ? 'config.' + GROUP_BY_ALL_KEY.name : k
      ) ?? [])
    : [];

  // Note: We don't care about special-casing the baseline run id here, because
  // baseline runs are always pinned. Adding the baseline to this array would
  // double-count it for the purposes of setting the page size/limits.
  const pinnedRuns = query.pinnedRunIds ?? [];
  const isGrouped = groupKeys.length > 0;
  const useGroupingDepth = [
    'GroupedBucketedQuery',
    'GroupedBucketedQueryId',
    'GroupedBucketedBackendLinesQuery',
  ].includes(queryType);
  const isSingleRunMode = !!runsDataQuery.runName;
  // Pinned sub-queries are only used in non-grouped, non-single-run mode.
  // Grouped views aggregate runs together so individual pin/baseline markers
  // don't apply. Single-run mode only fetches one specific run.
  const usePinnedSubQuery =
    !isSingleRunMode && !isGrouped && pinnedRuns.length > 0;

  let filters: string;
  let pinnedRunFilters: string | undefined;
  let pinnedRunsLimit = 0;
  let includePinnedRuns = false;

  if (usePinnedSubQuery) {
    // Determine which pinned runs are currently visible (not hidden by the user).
    // We check each pinned run name against the selections filter so we can
    // reduce the main query limit by the exact number that will be returned by
    // the sub-query, keeping the total number of lines shown constant.
    const pinnedRunNames = runIdsToNames(pinnedRuns);
    const selections = query.selections ?? Filter.And([]);
    // `query.selections` is always a name-based filter produced by
    // selectionmanager.toRunFilter, which only emits RUN_NAME_KEY predicates.
    // We therefore only need to supply `name` to satisfy Filter.match —
    // non-name keys would return null/undefined, but they never appear here.
    const visiblePinnedCount = pinnedRunNames.filter(name =>
      Filter.match(selections, {name} as Run.RunWithSupportedKeys)
    ).length;

    // Build the pinned sub-query filter: all pinned/baseline run names AND'd
    // with the visibility selections so hidden runs are not fetched.
    const rawPinnedFilter = pinnedRunIdsFilter(pinnedRuns);
    const pinnedFilter = query.selections
      ? Filter.And([rawPinnedFilter, query.selections])
      : rawPinnedFilter;
    pinnedRunFilters = JSON.stringify(Filter.toMongo(pinnedFilter));
    pinnedRunsLimit = pinnedRuns.length;
    includePinnedRuns = true;

    // Exclude pinned/baseline from the main query to prevent double-counting.
    // The exclusion is applied before selections so the final filter is
    // AND(mainFilters, excludePinned, selections).
    const excludePinned = excludedRunIdsFilter(pinnedRuns)!;
    const mainBase = Filter.And([query.filters, excludePinned]);
    filters = JSON.stringify(
      Filter.toMongo(
        query.selections ? Filter.And([mainBase, query.selections]) : mainBase
      )
    );

    // Shrink the main limit to leave room for the visible pinned runs so the
    // total number of lines in the chart stays at the configured page size.
    limit = Math.max(0, limit - visiblePinnedCount);
  } else {
    filters = JSON.stringify(
      Filter.toMongo(
        query.selections
          ? Filter.And([query.filters, query.selections])
          : query.filters
      )
    );
  }

  return {
    bins: nBuckets,
    bucketedHistorySpecs:
      runsDataQuery.bucketedHistorySpecs?.map(bhs =>
        JSON.stringify({
          ...bhs,
          bins: nBuckets,
          xAxis: bhs.xAxis ?? '_step',
        })
      ) ?? [],
    configKeys: runsDataQuery.configKeys,
    enableConfig: runsDataQuery.configKeys != null,
    enableSummary: runsDataQuery.summaryKeys != null,
    entityName: query.entityName,
    excludeOutliers: runsDataQuery.excludeOutliers,
    expressions,
    filters,
    groupKeys,
    groupLevel: 0,
    groupingDepth:
      useGroupingDepth && Number.isFinite(runsDataQuery.groupingDepth)
        ? 1
        : undefined,
    includePinnedRuns,
    internalId: query.internalProjectId ?? '',
    isSystemMetrics:
      runsDataQuery.bucketedHistorySpecs?.every(bhs =>
        bhs.keys.some(isSystemMetric)
      ) ?? null,
    limit,
    maxGroups: query.maxGroups,
    order,
    panelType,
    pinnedRunFilters,
    pinnedRunsLimit,
    projectName: query.projectName,
    summaryKeys: runsDataQuery.summaryKeys,
    timeout,
    primaryLineType: runsDataQuery.primaryLineType,
    xAxisScale: runsDataQuery.xAxisScale,
  };
}
